import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import Loader from '../../Component/Loader';
import './Notifications.css';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingAsRead, setMarkingAsRead] = useState({});
    const [filter, setFilter] = useState('all'); // all, unread, read
    const [hoveredId, setHoveredId] = useState(null);

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.apiUrl}/notification`, {
                headers: {
                    Authorization: config.token,
                },
            });

            if (response.data.success) {
                setNotifications(response.data.list);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            toast.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        setMarkingAsRead(prev => ({ ...prev, [id]: true }));

        try {
            const response = await axios.put(
                `${config.apiUrl}/notification/${id}`,
                {},
                {
                    headers: {
                        Authorization: config.token,
                    },
                }
            );

            if (response.data.success) {
                // Update the notification in the local state
                setNotifications(prevNotifications =>
                    prevNotifications.map(notification =>
                        notification.id === id
                            ? { ...notification, is_read: 1 }
                            : notification
                    )
                );
                toast.success('Notification marked as read');
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
            toast.error('Failed to mark notification as read');
        } finally {
            setMarkingAsRead(prev => ({ ...prev, [id]: false }));
        }
    };

    const markAllAsRead = async () => {
        const unreadNotifications = notifications.filter(n => n.is_read === 0);

        if (unreadNotifications.length === 0) {
            toast.info('All notifications are already read');
            return;
        }

        try {
            // Mark all unread notifications as read
            await Promise.all(
                unreadNotifications.map(notification =>
                    axios.put(
                        `${config.apiUrl}/notification/${notification.id}`,
                        {},
                        {
                            headers: {
                                Authorization: config.token,
                            },
                        }
                    )
                )
            );

            // Update all notifications to read
            setNotifications(prevNotifications =>
                prevNotifications.map(notification => ({
                    ...notification,
                    is_read: 1,
                }))
            );

            toast.success('All notifications marked as read');
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            toast.error('Failed to mark all notifications as read');
        }
    };

    const getNotificationIcon = (type) => {
        const iconConfig = {
            product_enquiry: { icon: 'bi-box-seam', color: '#cc0000', bg: '#fff5f5' },
            order: { icon: 'bi-cart-check', color: '#059669', bg: '#d1fae5' },
            payment: { icon: 'bi-credit-card', color: '#990000', bg: '#ffe6e6' },
            message: { icon: 'bi-chat-dots', color: '#cc0000', bg: '#fff0f0' },
            default: { icon: 'bi-bell', color: '#cc0000', bg: '#fff5f5' },
        };

        const config = iconConfig[type] || iconConfig.default;
        return (
            <div
                className="notification-icon-wrapper"
                style={{ backgroundColor: config.bg, color: config.color }}
            >
                <i className={`bi ${config.icon}`}></i>
            </div>
        );
    };

    const getNotificationCategory = (type) => {
        const categories = {
            product_enquiry: { label: 'Product', color: '#cc0000' },
            order: { label: 'Order', color: '#059669' },
            payment: { label: 'Payment', color: '#990000' },
            message: { label: 'Message', color: '#cc0000' },
        };
        return categories[type] || { label: 'General', color: '#cc0000' };
    };

    const getTimeAgo = (date) => {
        if (!date) return 'Recently';
        const now = new Date();
        const notificationDate = new Date(date);
        const diffInSeconds = Math.floor((now - notificationDate) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        // Show both date and time for older notifications
        return notificationDate.toLocaleString();
    };

    const groupNotificationsByDate = (notifications) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const groups = {
            today: [],
            yesterday: [],
            older: [],
        };

        notifications.forEach(notification => {
            const notifDate = notification.created_at ? new Date(notification.created_at) : new Date();
            notifDate.setHours(0, 0, 0, 0);

            if (notifDate.getTime() === today.getTime()) {
                groups.today.push(notification);
            } else if (notifDate.getTime() === yesterday.getTime()) {
                groups.yesterday.push(notification);
            } else {
                groups.older.push(notification);
            }
        });

        return groups;
    };

    if (loading) {
        return <Loader />;
    }

    const unreadCount = notifications.filter(n => n.is_read === 0).length;
    const filteredNotifications = notifications.filter(n => {
        if (filter === 'unread') return n.is_read === 0;
        if (filter === 'read') return n.is_read === 1;
        return true;
    });

    const groupedNotifications = groupNotificationsByDate(filteredNotifications);

    const renderNotificationGroup = (title, notificationList) => {
        if (notificationList.length === 0) return null;

        return (
            <div className="notification-group">
                <div className="notification-group-header">
                    <h6>{title}</h6>
                    <span className="notification-group-count">{notificationList.length}</span>
                </div>
                {notificationList.map((notification) => {
                    const category = getNotificationCategory(notification.type);
                    return (
                        <div
                            key={notification.id}
                            className={`notification-item ${notification.is_read === 0 ? 'unread' : 'read'
                                } ${hoveredId === notification.id ? 'hovered' : ''}`}
                            onMouseEnter={() => setHoveredId(notification.id)}
                            onMouseLeave={() => setHoveredId(null)}
                        >
                            {getNotificationIcon(notification.type)}

                            <div className="notification-content">
                                <div className="notification-header">
                                    <div className="notification-title-wrapper">
                                        <h6 className="notification-title">
                                            {notification.title}
                                        </h6>
                                        <span
                                            className="notification-category-badge"
                                            style={{ backgroundColor: category.color }}
                                        >
                                            {category.label}
                                        </span>
                                    </div>
                                    {notification.is_read === 0 && (
                                        <span className="unread-dot"></span>
                                    )}
                                </div>

                                <p className="notification-body">
                                    {notification.body}
                                </p>

                                <div className="notification-footer">
                                    <div className="notification-time">
                                        <i className="bi bi-clock"></i>
                                        <span>
                                            {getTimeAgo(notification.created_at)}
                                            {/* {notification.created_at && (
                                                <>
                                                    <br />
                                                    <span className="notification-datetime">
                                                        {new Date(notification.created_at).toLocaleDateString()} {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </>
                                            )} */}
                                        </span>
                                    </div>

                                    {notification.is_read === 0 && (
                                        <button
                                            className="mark-read-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAsRead(notification.id);
                                            }}
                                            disabled={markingAsRead[notification.id]}
                                        >
                                            {markingAsRead[notification.id] ? (
                                                <span className="spinner-border spinner-border-sm"></span>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check2"></i>
                                                    Mark as read
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="notifications-container">
            <div className="container">
                <div className="row">
                    <div className="col-lg-10 mx-auto">
                        <div className="notifications-card">
                            <div className="notifications-header">
                                <div className="header-title-section">
                                    <h3 className="header-title text-white">
                                        <i className="bi bi-bell-fill"></i>
                                        Notifications
                                    </h3>
                                    {unreadCount > 0 && (
                                        <span className="unread-count-badge">
                                            {unreadCount} unread
                                        </span>
                                    )}
                                </div>

                                <div className="header-actions">
                                    {unreadCount > 0 && (
                                        <button
                                            className="mark-all-btn"
                                            onClick={markAllAsRead}
                                        >
                                            <i className="bi bi-check-all"></i>
                                            Mark all as read
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="filter-tabs">
                                <button
                                    className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                                    onClick={() => setFilter('all')}
                                >
                                    All
                                    <span className="tab-count">{notifications.length}</span>
                                </button>
                                <button
                                    className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
                                    onClick={() => setFilter('unread')}
                                >
                                    Unread
                                    <span className="tab-count">{unreadCount}</span>
                                </button>
                                <button
                                    className={`filter-tab ${filter === 'read' ? 'active' : ''}`}
                                    onClick={() => setFilter('read')}
                                >
                                    Read
                                    <span className="tab-count">{notifications.filter(n => n.is_read === 1).length}</span>
                                </button>
                            </div>

                            <div className="notifications-body">
                                {filteredNotifications.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-state-icon">
                                            <i className="bi bi-bell-slash"></i>
                                        </div>
                                        <h5>No notifications here</h5>
                                        <p>When you receive notifications, they'll appear here</p>
                                    </div>
                                ) : (
                                    <div className="notifications-list">
                                        {renderNotificationGroup('Today', groupedNotifications.today)}
                                        {renderNotificationGroup('Yesterday', groupedNotifications.yesterday)}
                                        {renderNotificationGroup('Older', groupedNotifications.older)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Notifications;
